import React, {useEffect} from 'react';
import {Typography, Box, MenuItem} from '@mui/material';
import Input from '$components/Input';
import SelectInput from '$components/SelectInput';
import {useFormik} from 'formik';
import * as Yup from 'yup';
import {InsuranceClaim} from '$api/finance/insuranceClaims/insuranceClaimDetails';
import {useUpdateInsuranceClaim} from '../hooks';
import {useInsuranceTypeHook} from '~/modules/frontdesk/Calendar/OfflineBooking/hooks/useInsuranceTypeHook';
import Loading from '~/components/Loading';
import FetchError from '~/components/FetchError';
import PricingListCommonWrapperModalComponent from '~/modules/frontdesk/Finance/Dashboard/PricingList/PricingListCommonWrapperModalComponent';
import {Color} from '$constants/style';
import {
  EDIT_CLAIM_LABELS,
  EDIT_CLAIM_PLACEHOLDERS,
  EDIT_CLAIM_BUTTONS,
  EDIT_CLAIM_VALIDATION_MESSAGES,
  EDIT_CLAIM_DIALOG_CONFIG,
} from '../constants';
import {
  editClaimDialogContainerStyles,
  editClaimFieldContainerStyles,
  editClaimFieldLabelStyles,
  editClaimSelectInputStyles,
  editClaimErrorTextStyles,
} from './style';

interface EditClaimDialogProps {
  claim: InsuranceClaim;
  open: boolean;
  onClose: () => void;
  onUpdate?: () => void;
}

interface EditClaimFormData {
  insurance_id: number;
  membership_no: string;
  pre_authorisation_code: string;
}

const validationSchema = Yup.object({
  insurance_id: Yup.number()
    .min(1, EDIT_CLAIM_VALIDATION_MESSAGES.INSURANCE_PROVIDER_REQUIRED)
    .required(EDIT_CLAIM_VALIDATION_MESSAGES.INSURANCE_PROVIDER_REQUIRED)
    .test(
      'not-zero',
      EDIT_CLAIM_VALIDATION_MESSAGES.INSURANCE_PROVIDER_REQUIRED,
      value => value > 0
    ),
  membership_no: Yup.string()
    .trim()
    .required(EDIT_CLAIM_VALIDATION_MESSAGES.MEMBERSHIP_NUMBER_REQUIRED)
    .min(1, EDIT_CLAIM_VALIDATION_MESSAGES.MEMBERSHIP_NUMBER_REQUIRED)
    .max(12, 'Membership number cannot exceed 12 digits'),
  pre_authorisation_code: Yup.string().optional(),
});

const EditClaimDialog: React.FC<EditClaimDialogProps> = ({
  claim,
  open,
  onClose,
  onUpdate,
}) => {
  const {sendUpdateInsuranceClaimRequest, isPending, disableSubmit} =
    useUpdateInsuranceClaim();
  const {
    data: insuranceTypes,
    isPending: isInsuranceTypesLoading,
    isError: isInsuranceTypesError,
  } = useInsuranceTypeHook();

  const formik = useFormik<EditClaimFormData>({
    initialValues: {
      insurance_id: claim.insurance_id || 0,
      membership_no: claim.membership_no || '',
      pre_authorisation_code: claim.pre_authorisation_code || '',
    },
    validationSchema,
    validateOnMount: true,
    enableReinitialize: true,
    onSubmit: async values => {
      await sendUpdateInsuranceClaimRequest(claim.id.toString(), {
        insurance_claim: values,
      });
      onUpdate?.();
      onClose();
    },
  });

  const handleClose = () => {
    formik.resetForm();
    onClose();
  };

  const handleSave = () => {
    formik.handleSubmit();
  };

  const areMandatoryFieldsFilled = () => {
    return (
      formik.values.insurance_id > 0 &&
      formik.values.membership_no.trim().length > 0
    );
  };

  useEffect(() => {
    formik.validateForm();
  }, [formik.values.insurance_id, formik.values.membership_no]);

  const renderInsuranceProviderItem = (item: {id: number; name: string}) => (
    <MenuItem key={item.id} value={item.id.toString()}>
      {item.name}
    </MenuItem>
  );

  const renderInsuranceProviderValue = (value: string) => {
    if (!insuranceTypes?.data)
      return (
        <span style={{color: Color.TextPrimayPlaceholder}}>
          {EDIT_CLAIM_LABELS.SELECT_INSURANCE_PROVIDER}
        </span>
      );
    const provider = insuranceTypes.data.find(p => p.id.toString() === value);
    return provider ? (
      provider.name
    ) : (
      <span style={{color: Color.TextPrimayPlaceholder}}>
        {EDIT_CLAIM_LABELS.SELECT_INSURANCE_PROVIDER}
      </span>
    );
  };

  if (isInsuranceTypesLoading) return <Loading />;
  if (isInsuranceTypesError || !insuranceTypes?.data) return <FetchError />;

  const insuranceProviders = insuranceTypes.data;

  return (
    <PricingListCommonWrapperModalComponent
      isOpen={open}
      onClose={handleClose}
      title={EDIT_CLAIM_LABELS.TITLE}
      width={EDIT_CLAIM_DIALOG_CONFIG.WIDTH}
      onPrimaryClick={handleSave}
      primaryButtonTitle={EDIT_CLAIM_BUTTONS.SAVE}
      isPrimaryDisabled={
        isPending ||
        disableSubmit ||
        !formik.isValid ||
        !areMandatoryFieldsFilled()
      }
      isSecondaryVisible={true}
      secondaryButtonTitle={EDIT_CLAIM_BUTTONS.CANCEL}
      onSecondaryClick={handleClose}
    >
      <Box sx={editClaimDialogContainerStyles}>
        <Box sx={editClaimFieldContainerStyles}>
          <Typography variant="body2" sx={editClaimFieldLabelStyles}>
            {EDIT_CLAIM_LABELS.INSURANCE_PROVIDER}
          </Typography>
          <Box sx={editClaimSelectInputStyles}>
            <SelectInput
              items={insuranceProviders}
              renderItem={renderInsuranceProviderItem}
              renderValue={renderInsuranceProviderValue}
              onChange={value =>
                formik.setFieldValue('insurance_id', parseInt(value))
              }
              value={formik.values.insurance_id.toString()}
              error={Boolean(
                formik.touched.insurance_id && formik.errors.insurance_id
              )}
              fullWidth
            />
          </Box>
          {formik.touched.insurance_id && formik.errors.insurance_id && (
            <Typography
              variant="caption"
              color="error"
              sx={editClaimErrorTextStyles}
            >
              {formik.errors.insurance_id}
            </Typography>
          )}
        </Box>

        <Box sx={editClaimFieldContainerStyles}>
          <Typography variant="body2" sx={editClaimFieldLabelStyles}>
            {EDIT_CLAIM_LABELS.MEMBERSHIP_NUMBER}
          </Typography>
          <Input
            name="membership_no"
            placeholder={EDIT_CLAIM_PLACEHOLDERS.MEMBERSHIP_NUMBER}
            value={formik.values.membership_no}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(
              formik.touched.membership_no && formik.errors.membership_no
            )}
            helperText={
              formik.touched.membership_no && formik.errors.membership_no
                ? formik.errors.membership_no
                : ''
            }
            touched={formik.touched.membership_no}
          />
        </Box>

        <Box sx={editClaimFieldContainerStyles}>
          <Typography variant="body2" sx={editClaimFieldLabelStyles}>
            {EDIT_CLAIM_LABELS.PRE_AUTHORISATION_CODE}
          </Typography>
          <Input
            name="pre_authorisation_code"
            placeholder={EDIT_CLAIM_PLACEHOLDERS.PRE_AUTHORISATION_CODE}
            value={formik.values.pre_authorisation_code}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={Boolean(
              formik.touched.pre_authorisation_code &&
                formik.errors.pre_authorisation_code
            )}
            helperText={
              formik.touched.pre_authorisation_code &&
              formik.errors.pre_authorisation_code
                ? formik.errors.pre_authorisation_code
                : ''
            }
            touched={formik.touched.pre_authorisation_code}
          />
        </Box>
      </Box>
    </PricingListCommonWrapperModalComponent>
  );
};

export default EditClaimDialog;
